import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { CircularProgress, IconButton, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { DataGrid, type GridColDef, type GridRenderCellParams, type GridSortModel } from "@mui/x-data-grid";
import type { DateTime } from "luxon";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CommonDashboardBox } from "../../../components/Dashboard/CommonDashboardBox";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useDepartmentFlags } from "../../../hooks/useDepartmentFlags";
import { useExpandedGroups } from "../../../hooks/useExpandedGroups";
import { type CostDeepDiveResponse, CostDeepDiveUnit, type OtMode } from "../../../utils/models";
import { formatCurrency } from "../../../utils/utilFunctions";
import { buildAugmentedRows, buildClampRange, isWeekKey, MC_GRID_SX, type MCTableAugmentedRow, sortRowsByModel, weekKeyToDateRange } from "../utils";
import { CostDeepDiveExpandedRow } from "./CostDeepDiveExpandedRow";
import type { CostDayRow } from "./types";
import { buildCostDayRows, formatHours, showsConsecutiveOt } from "./utils";

type Props = {
	data?: CostDeepDiveResponse;
	isActual: boolean;
	isLoading: boolean;
	isWeekly: boolean;
	sinceDate: DateTime;
	endDate?: DateTime;
	roles?: string[];
	jobs?: string[];
	laborCost?: boolean;
	shiftHours?: string[];
	includeLateScheduled?: boolean;
	unit?: CostDeepDiveUnit;
	otMode?: OtMode;
	estimateMissingWage: boolean;
};

type AugmentedRow = MCTableAugmentedRow<CostDayRow>;

// Column count excludes expand; used to compute detail-row colSpan.
// Always 5: date, base, weeklyOT, agency, total. +1 for dailyOT when present, +1 for bonus when present, +1 for PTO (cost mode only).
const BASE_COLUMNS = 5;
const TOTALS_ROW_ID = "__totals__";
const EMPTY_COST_ROW: Omit<CostDayRow, "date"> = {
	dateDisplay: "",
	baseCost: 0,
	dailyOtCost: 0,
	weeklyOtCost: 0,
	consecutiveOtCost: 0,
	agencyCost: 0,
	bonusCost: 0,
	ptoCost: 0,
	totalCost: 0,
	baseHours: 0,
	dailyOtHours: 0,
	weeklyOtHours: 0,
	consecutiveOtHours: 0,
	agencyHours: 0,
	totalHours: 0,
};

export const CostDeepDiveTable: FC<Props> = ({
	data,
	isActual,
	isLoading,
	isWeekly,
	sinceDate,
	endDate,
	roles,
	jobs,
	shiftHours,
	laborCost,
	includeLateScheduled,
	unit = CostDeepDiveUnit.Cost,
	otMode,
	estimateMissingWage,
}) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { timezone } = useDepartmentFlags();
	const { expandedGroups, toggleGroupExpand } = useExpandedGroups();
	const [sortModel, setSortModel] = useState<GridSortModel>([{ field: "dateDisplay", sort: "desc" }]);

	const hasBonuses = useMemo(() => (data ? Object.values(data.bonusCost).some((v) => v > 0) : false), [data]);
	const hasDailyOt = useMemo(() => {
		if (!data) return false;
		if (unit === CostDeepDiveUnit.Hours) return Object.values(data.dailyOtHours ?? {}).some((v) => v > 0);
		return Object.values(data.dailyOtCost).some((v) => v > 0);
	}, [data, unit]);

	// DAT-289: gated on whether the department CONFIGURES a consecutive-day rule, not on whether any
	// landed in the visible range — unlike `hasDailyOt` above. Scanning values would make the column
	// blink in and out as the user pages between weeks, since a 7th consecutive day is occasional
	// rather than weekly. The figures themselves are always computed (0.00 without a rule), so a
	// department that adopts one later gets correct history with no backfill.
	const hasConsecutiveOt = showsConsecutiveOt(data);

	const clampRange = useMemo(() => buildClampRange(sinceDate, endDate, timezone), [sinceDate, endDate, timezone]);
	const dateRange = useMemo(
		// Daily mode fills a row per day across the range; weekly mode reads the week-bucket keys as-is.
		() => (isWeekly ? undefined : clampRange),
		[isWeekly, clampRange],
	);
	const rows = useMemo(() => buildCostDayRows(data, timezone, dateRange, clampRange), [data, timezone, dateRange, clampRange]);
	const sortedRows = useMemo(() => sortRowsByModel(rows, sortModel), [rows, sortModel]);
	const augmentedRows = useMemo(() => buildAugmentedRows(sortedRows, expandedGroups, EMPTY_COST_ROW), [sortedRows, expandedGroups]);

	const totalsRow = useMemo((): AugmentedRow => {
		const sumField = (field: keyof CostDayRow): number => rows.reduce((acc, r) => acc + (r[field] as number), 0);
		return {
			date: TOTALS_ROW_ID,
			dateDisplay: t("cost.deep.dive.period.total"),
			baseCost: sumField("baseCost"),
			dailyOtCost: sumField("dailyOtCost"),
			weeklyOtCost: sumField("weeklyOtCost"),
			consecutiveOtCost: sumField("consecutiveOtCost"),
			agencyCost: sumField("agencyCost"),
			bonusCost: sumField("bonusCost"),
			ptoCost: sumField("ptoCost"),
			totalCost: sumField("totalCost"),
			baseHours: sumField("baseHours"),
			dailyOtHours: sumField("dailyOtHours"),
			weeklyOtHours: sumField("weeklyOtHours"),
			consecutiveOtHours: sumField("consecutiveOtHours"),
			agencyHours: sumField("agencyHours"),
			totalHours: sumField("totalHours"),
			_isDetail: false,
			_parentDate: "",
		};
	}, [rows, t]);

	const rowsWithTotals = useMemo(() => (rows.length > 0 ? [totalsRow, ...augmentedRows] : augmentedRows), [totalsRow, augmentedRows, rows.length]);

	const handleRowClick = useCallback(
		(params: { id: string | number }) => {
			const id = params.id as string;
			if (id === TOTALS_ROW_ID) return;
			if (id.endsWith("-detail")) return;
			const isExpanding = !expandedGroups.has(id);
			toggleGroupExpand(id);
			if (isExpanding) {
				track("cost_deep_dive_row_expanded", { date: id });
			}
		},
		[expandedGroups, toggleGroupExpand, track],
	);

	const columns: GridColDef<AugmentedRow>[] = useMemo(() => {
		const isHours = unit === CostDeepDiveUnit.Hours;
		const formatValue = (value: number) => (isHours ? formatHours(value) : formatCurrency(value, 1));
		// Bonus shows in any unit mode (incentive pay, in $) so hide-wage users still see it. PTO is cost-mode only.
		const includePtoCol = !isHours && !!laborCost;
		const detailColSpan = BASE_COLUMNS + (hasDailyOt ? 1 : 0) + (hasConsecutiveOt ? 1 : 0) + (hasBonuses ? 1 : 0) + (includePtoCol ? 1 : 0);

		return [
			{
				field: "expand",
				headerName: "",
				width: 36,
				sortable: false,
				disableColumnMenu: true,
				resizable: false,
				display: "flex",
				colSpan: (_value: unknown, row: AugmentedRow) => (row._isDetail ? detailColSpan : 1),
				renderCell: (params: GridRenderCellParams<AugmentedRow>) => {
					if (params.row._isDetail) {
						const parentDate = params.row._parentDate;
						const weekRange = isWeekKey(parentDate) ? weekKeyToDateRange(parentDate) : undefined;
						return (
							<CostDeepDiveExpandedRow
								date={weekRange?.start ?? parentDate}
								endDate={weekRange?.end}
								isActual={isActual}
								roles={roles}
								jobs={jobs}
								shiftHours={shiftHours}
								laborCost={laborCost}
								hasBonuses={hasBonuses}
								hasDailyOt={hasDailyOt}
								hasConsecutiveOt={hasConsecutiveOt}
								includeLateScheduled={includeLateScheduled}
								unit={unit}
								otMode={otMode}
								estimateMissingWage={estimateMissingWage}
							/>
						);
					}
					if (params.row.date === TOTALS_ROW_ID) return null;
					const isExpanded = expandedGroups.has(params.row.date);
					return (
						<IconButton
							size="small"
							onClick={(e) => {
								e.stopPropagation();
								handleRowClick({ id: params.row.date });
							}}
							aria-label={isExpanded ? t("cost.deep.dive.collapse") : t("cost.deep.dive.expand")}
							aria-expanded={isExpanded}
							tabIndex={-1}
						>
							{isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
						</IconButton>
					);
				},
			},
			{
				field: "dateDisplay",
				headerName: t("cost.deep.dive.date"),
				flex: 1.4,
				minWidth: 140,
			},
			{
				field: isHours ? "baseHours" : "baseCost",
				headerName: isHours ? t("cost.deep.dive.hours.base") : t("cost.deep.dive.base"),
				flex: 1,
				minWidth: 100,
				align: "center",
				headerAlign: "center",
				type: "number",
				valueFormatter: formatValue,
			},
			...(hasDailyOt
				? [
						{
							field: isHours ? "dailyOtHours" : "dailyOtCost",
							headerName: isHours ? t("cost.deep.dive.hours.daily.ot") : t("cost.deep.dive.daily.ot"),
							flex: 1,
							minWidth: 100,
							align: "center" as const,
							headerAlign: "center" as const,
							type: "number" as const,
							valueFormatter: formatValue,
						},
					]
				: []),
			{
				field: isHours ? "weeklyOtHours" : "weeklyOtCost",
				headerName: isHours ? t("cost.deep.dive.hours.weekly.ot") : t("cost.deep.dive.weekly.ot"),
				flex: 1,
				minWidth: 100,
				align: "center",
				headerAlign: "center",
				type: "number",
				valueFormatter: formatValue,
			},
			...(hasConsecutiveOt
				? [
						{
							field: isHours ? "consecutiveOtHours" : "consecutiveOtCost",
							headerName: isHours ? t("cost.deep.dive.hours.consecutive.ot") : t("cost.deep.dive.consecutive.ot"),
							flex: 1,
							minWidth: 100,
							align: "center" as const,
							headerAlign: "center" as const,
							type: "number" as const,
							valueFormatter: formatValue,
						},
					]
				: []),
			{
				field: isHours ? "agencyHours" : "agencyCost",
				headerName: isHours ? t("cost.deep.dive.hours.agency") : t("cost.deep.dive.agency"),
				flex: 1,
				minWidth: 100,
				align: "center",
				headerAlign: "center",
				type: "number",
				valueFormatter: formatValue,
			},
			...(hasBonuses
				? [
						{
							field: "bonusCost",
							headerName: t("cost.deep.dive.bonus"),
							flex: 1,
							minWidth: 100,
							align: "center" as const,
							headerAlign: "center" as const,
							type: "number" as const,
							valueFormatter: (value: number) => formatCurrency(value, 1),
						},
					]
				: []),
			...(includePtoCol
				? [
						{
							field: "ptoCost",
							headerName: t("pto"),
							flex: 1,
							minWidth: 100,
							align: "center" as const,
							headerAlign: "center" as const,
							type: "number" as const,
							valueFormatter: (value: number) => formatCurrency(value, 1),
						},
					]
				: []),
			{
				field: isHours ? "totalHours" : "totalCost",
				headerName: isHours ? t("cost.deep.dive.hours.total") : t("cost.deep.dive.total"),
				flex: 1,
				minWidth: 100,
				align: "center",
				headerAlign: "center",
				type: "number",
				display: "flex",
				renderCell: (params: GridRenderCellParams<AugmentedRow>) => (
					<Typography
						variant="body2"
						sx={{
							fontWeight: 600,
							lineHeight: "inherit",
						}}
					>
						{formatValue(isHours ? params.row.totalHours : params.row.totalCost)}
					</Typography>
				),
			},
		];
	}, [
		t,
		expandedGroups,
		isActual,
		handleRowClick,
		roles,
		jobs,
		shiftHours,
		laborCost,
		hasBonuses,
		hasDailyOt,
		includeLateScheduled,
		unit,
		otMode,
		estimateMissingWage,
		hasConsecutiveOt,
	]);

	const handleSortModelChange = useCallback(
		(model: GridSortModel) => {
			setSortModel(model);
			if (model.length > 0) {
				track("cost_deep_dive_sort_changed", { column: model[0].field, direction: model[0].sort });
			}
		},
		[track],
	);

	if (isLoading) {
		return (
			<CommonDashboardBox sx={{ flex: 1, minWidth: 0 }}>
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						py: 4,
					}}
				>
					<CircularProgress size={24} />
				</Box>
			</CommonDashboardBox>
		);
	}

	if (rows.length === 0) {
		return (
			<CommonDashboardBox sx={{ flex: 1, minWidth: 0 }}>
				<Typography
					variant="body2"
					sx={{
						color: "text.secondary",
						textAlign: "center",
						py: 4,
					}}
				>
					{t("cost.deep.dive.no.data")}
				</Typography>
			</CommonDashboardBox>
		);
	}

	return (
		<CommonDashboardBox
			sx={{
				minWidth: 0,
				flex: 1,
				overflow: "hidden",
				height: "100%",
			}}
		>
			<DataGrid
				rows={rowsWithTotals}
				columns={columns}
				getRowId={(row) => row.date}
				sortingMode="server"
				sortModel={sortModel}
				onSortModelChange={handleSortModelChange}
				onRowClick={handleRowClick}
				getRowHeight={({ id }) => ((id as string).endsWith("-detail") ? "auto" : undefined)}
				density="compact"
				disableColumnMenu
				disableColumnFilter
				hideFooter
				rowSelection={false}
				sx={{
					...MC_GRID_SX,
					maxHeight: "60vh",
					"& .MuiDataGrid-row": {
						cursor: "pointer",
					},
					"& [data-id='__totals__']": {
						backgroundColor: "#EEF0F7",
						borderBottom: "2px solid #CDD2E8",
						cursor: "default",
						"& .MuiDataGrid-cell": {
							fontWeight: 700,
						},
					},
					"& [data-id='__totals__']:hover": {
						backgroundColor: "#EEF0F7 !important",
					},
				}}
			/>
		</CommonDashboardBox>
	);
};
